import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Divider, 
  IconButton, 
  Tooltip, 
  TextField,
  Button,
  List,
  ListItem,
  Chip,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import { 
  Send as SendIcon,
  Person as PersonIcon,
  SmartToy as SmartToyIcon,
  Delete as DeleteIcon,
  QuestionAnswer as QuestionAnswerIcon,
  LightbulbOutlined as LightbulbOutlinedIcon
} from '@mui/icons-material';
import TimeSavedIndicator from '../TimeSavedIndicator';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'ai';
  timestamp: Date;
}

interface AiChatProps {
  videoTitle: string;
  timeSaved: number;
  onAskQuestion: (question: string, detailLevel: 'full' | 'quick') => Promise<string>;
  onGetSuggestedQuestions: () => Promise<string[]>;
  initialMessages?: Message[];
}

const AiChat: React.FC<AiChatProps> = ({
  videoTitle,
  timeSaved,
  onAskQuestion,
  onGetSuggestedQuestions,
  initialMessages = []
}) => {
  // State for messages
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  
  // State for input
  const [input, setInput] = useState('');
  
  // State for loading
  const [isLoading, setIsLoading] = useState(false);
  
  // State for suggested questions
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([]);
  
  // State for detail level
  const [detailLevel, setDetailLevel] = useState<'full' | 'quick'>('full');
  
  // Ref for message list
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Load suggested questions
  const loadSuggestedQuestions = useCallback(async () => {
    try {
      const questions = await onGetSuggestedQuestions();
      setSuggestedQuestions(questions);
    } catch (error) {
      console.error('Error loading suggested questions:', error);
    }
  }, [onGetSuggestedQuestions]);
  
  // Load suggested questions on mount
  useEffect(() => {
    loadSuggestedQuestions();
  }, [loadSuggestedQuestions]);
  
  // Handle input change
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInput(event.target.value);
  };
  
  // Handle send message
  const handleSendMessage = async () => {
    if (!input.trim()) return;
    
    // Add user message
    const userMessage: Message = {
      id: `user-${Date.now()}`,
      text: input,
      sender: 'user',
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    
    try {
      // Get AI response
      const response = await onAskQuestion(input, detailLevel);
      
      // Add AI message
      const aiMessage: Message = {
        id: `ai-${Date.now()}`,
        text: response,
        sender: 'ai',
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, aiMessage]);
      
      // Load new suggested questions
      loadSuggestedQuestions();
    } catch (error) {
      console.error('Error getting AI response:', error);
      
      // Add error message
      const errorMessage: Message = {
        id: `ai-error-${Date.now()}`,
        text: 'Sorry, I encountered an error while processing your question. Please try again.',
        sender: 'ai',
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle key press
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };
  
  // Handle suggested question click
  const handleSuggestedQuestionClick = (question: string) => {
    setInput(question);
  };
  
  // Handle clear chat
  const handleClearChat = () => {
    setMessages([]);
    loadSuggestedQuestions();
  };
  
  // Handle detail level change
  const handleDetailLevelChange = (_event: React.MouseEvent<HTMLElement>, newDetailLevel: 'full' | 'quick' | null) => {
    if (newDetailLevel !== null) {
      setDetailLevel(newDetailLevel);
    }
  };
  
  // Format timestamp
  const formatTimestamp = (date: Date): string => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  
  return (
    <Paper 
      elevation={0} 
      className="glass-container"
      sx={{ 
        p: 3,
        mb: 4,
        display: 'flex',
        flexDirection: 'column',
        height: 600
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <QuestionAnswerIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h5" component="h2">
            AI Chat
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TimeSavedIndicator minutes={timeSaved} />
          
          <Tooltip title="Clear chat">
            <IconButton onClick={handleClearChat} sx={{ ml: 1 }}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Ask questions about "{videoTitle}" and get instant answers.
      </Typography>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2">
          Response detail:
        </Typography>
        
        <ToggleButtonGroup
          value={detailLevel}
          exclusive
          onChange={handleDetailLevelChange}
          size="small"
          aria-label="response detail level"
        >
          <ToggleButton value="quick" aria-label="quick responses">
            Quick
          </ToggleButton>
          <ToggleButton value="full" aria-label="detailed responses">
            Detailed
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      
      <Divider sx={{ mb: 2 }} />
      
      {/* Messages */}
      <Box 
        sx={{ 
          flexGrow: 1, 
          overflowY: 'auto',
          mb: 2,
          px: 1
        }}
      >
        {messages.length === 0 ? (
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              opacity: 0.7
            }}
          >
            <QuestionAnswerIcon sx={{ fontSize: 48, mb: 2, color: 'text.secondary' }} />
            <Typography variant="body1" color="text.secondary" align="center">
              Ask a question about the video content to start chatting.
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
              Try one of the suggested questions below.
            </Typography>
          </Box>
        ) : (
          <List sx={{ width: '100%', p: 0 }}>
            {messages.map((message) => (
              <ListItem 
                key={message.id} 
                sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: message.sender === 'user' ? 'flex-end' : 'flex-start',
                  p: 1
                }}
              >
                <Box 
                  sx={{ 
                    display: 'flex',
                    alignItems: 'flex-start',
                    maxWidth: '80%'
                  }}
                >
                  {message.sender === 'ai' && (
                    <Box 
                      sx={{ 
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        borderRadius: '50%',
                        width: 32,
                        height: 32,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 1,
                        mt: 0.5
                      }}
                    >
                      <SmartToyIcon fontSize="small" />
                    </Box>
                  )}
                  
                  <Box>
                    <Box 
                      sx={{ 
                        bgcolor: message.sender === 'user' 
                          ? 'rgba(255, 158, 0, 0.1)' 
                          : 'rgba(0, 180, 216, 0.1)',
                        borderRadius: 2,
                        p: 2,
                        typography: 'body1',
                        '& p': { 
                          m: 0,
                          mb: 1,
                          '&:last-child': {
                            mb: 0
                          }
                        },
                        '& a': {
                          color: message.sender === 'user' 
                            ? 'secondary.main' 
                            : 'primary.main',
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline'
                          }
                        },
                        '& code': {
                          fontFamily: 'monospace',
                          bgcolor: 'rgba(255, 255, 255, 0.1)',
                          p: 0.5,
                          borderRadius: 0.5,
                        },
                        '& pre': {
                          fontFamily: 'monospace',
                          bgcolor: 'rgba(255, 255, 255, 0.05)',
                          p: 1,
                          borderRadius: 1,
                          overflowX: 'auto',
                          mb: 1,
                        },
                      }}
                    >
                      {message.sender === 'user' ? (
                        message.text
                      ) : (
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {message.text}
                        </ReactMarkdown>
                      )}
                    </Box>
                    
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ 
                        display: 'block',
                        mt: 0.5,
                        ml: message.sender === 'user' ? 'auto' : 0,
                        mr: message.sender === 'user' ? 0 : 'auto',
                      }}
                    >
                      {formatTimestamp(message.timestamp)}
                    </Typography>
                  </Box>
                  
                  {message.sender === 'user' && (
                    <Box 
                      sx={{ 
                        bgcolor: 'secondary.main',
                        color: 'secondary.contrastText',
                        borderRadius: '50%',
                        width: 32,
                        height: 32,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        ml: 1,
                        mt: 0.5
                      }}
                    >
                      <PersonIcon fontSize="small" />
                    </Box>
                  )}
                </Box>
              </ListItem>
            ))}
            <div ref={messagesEndRef} />
          </List>
        )}
      </Box>
      
      {/* Suggested questions */}
      {messages.length === 0 && suggestedQuestions.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <LightbulbOutlinedIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              Suggested questions:
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {suggestedQuestions.map((question, index) => (
              <Chip
                key={index}
                label={question}
                onClick={() => handleSuggestedQuestionClick(question)}
                color="primary"
                variant="outlined"
                sx={{ 
                  borderRadius: 4,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 180, 216, 0.1)',
                  }
                }}
              />
            ))}
          </Box>
        </Box>
      )}
      
      {/* Input */}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <TextField
          fullWidth
          placeholder="Ask a question about the video..."
          variant="outlined"
          value={input}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          disabled={isLoading}
          multiline
          maxRows={3}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 4,
            },
          }}
        />
        
        <Button
          variant="contained"
          color="primary"
          endIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
          onClick={handleSendMessage}
          disabled={!input.trim() || isLoading}
          sx={{ 
            ml: 1,
            borderRadius: 4,
            px: 3,
            py: 1.5,
            minWidth: 120
          }}
        >
          {isLoading ? 'Sending' : 'Send'}
        </Button>
      </Box>
    </Paper>
  );
};

export default AiChat;
